import { is8bitChar } from '../utils';

export const passwordValidator = (t, passwordPolicy, userId) => (input) => {
    let err = null;
    if (passwordPolicy.passwordCheckSyntax) {
        err = passwordMinLengthValidator(t, passwordPolicy.passwordMinLength)(input)
            || passwordMinAlphasValidator(t, passwordPolicy.passwordMinAlphas)(input)
            || passwordMinLowersValidator(t, passwordPolicy.passwordMinLowers)(input)
            || passwordMinUppersValidator(t, passwordPolicy.passwordMinUppers)(input)
            || passwordMinDigitsValidator(t, passwordPolicy.passwordMinDigits)(input)
            || passwordMinCategoriesValidator(t, passwordPolicy.passwordMinCategories)(input)
            || passwordMinSpecialsValidator(t, passwordPolicy.passwordMinSpecials)(input)
            || passwordMin8bitValidator(t, passwordPolicy.passwordMin8bit)(input)
            || passwordMaxRepeatsValidator(t, passwordPolicy.passwordMaxRepeats)(input)
            || passwordTokenLengthValidator(t, passwordPolicy.passwordMinTokenLength, userId)(input);
    }
    return err;
};

export const passwordMinLengthValidator = (t, minLength) => (input) => {
    let err = null;
    if (!input || input.length < minLength) {
        err = t('passwords.validation.min-length', { minLength });
    }
    return err;
};

export const passwordMinAlphasValidator = (t, minAlphas) => (input) => {
    let err = null;
    const alphasFound = (input.match(/[a-zA-Z]/g) || []).length;
    if (alphasFound < minAlphas) {
        err = t('passwords.validation.min-alphas', { minAlphas, alphasFound });
    }
    return err;
};

const LOWER_CHAR_REGEX = /[a-z]/g;
export const passwordMinLowersValidator = (t, minLowers) => (input) => {
    let err = null;
    const lowersFound = (input.match(LOWER_CHAR_REGEX) || []).length;
    if (lowersFound < minLowers) {
        err = t('passwords.validation.min-lowers', { minLowers, lowersFound });
    }
    return err;
};

const UPPER_CHAR_REGEX = /[A-Z]/g;
export const passwordMinUppersValidator = (t, minUppers) => (input) => {
    let err = null;
    const uppersFound = (input.match(UPPER_CHAR_REGEX) || []).length;
    if (uppersFound < minUppers) {
        err = t('passwords.validation.min-uppers', { minUppers, uppersFound });
    }
    return err;
};

const DIGIT_CHAR_REGEX = /\d/g;
export const passwordMinDigitsValidator = (t, minDigits) => (input) => {
    let err = null;
    const digitsFound = (input.match(DIGIT_CHAR_REGEX) || []).length;
    if (digitsFound < minDigits) {
        err = t('passwords.validation.min-digits', { minDigits, digitsFound });
    }
    return err;
};

const SPECIAL_CHAR_REGEX = /[_;,@#$%^&+=]/g;
export const passwordMinSpecialsValidator = (t, minSpecials) => (input) => {
    let err = null;
    const specialsFound = (input.match(SPECIAL_CHAR_REGEX) || []).length;
    if (specialsFound < minSpecials) {
        err = t('passwords.validation.min-specials', { minSpecials, specialsFound });
    }
    return err;
};

export const passwordMin8bitValidator = (t, min8bit) => (input) => {
    let err = null;
    const eightBitFound = [...input].reduce((acc, currentChar) => (is8bitChar(currentChar) ? acc + 1 : acc), 0);
    if (eightBitFound < min8bit) {
        err = t('passwords.validation.min-8bit', { min8bit, eightBitFound });
    }
    return err;
};

export const passwordMinCategoriesValidator = (t, minCategories) => (input) => {
    let err = null;
    let categoriesFound = [LOWER_CHAR_REGEX, UPPER_CHAR_REGEX, DIGIT_CHAR_REGEX, SPECIAL_CHAR_REGEX]
        .map((regEx) => input.match(regEx))
        .filter((matchResult) => matchResult !== null)
        .length;
    if ([...input].some(is8bitChar)) {
        categoriesFound++;
    }
    if (categoriesFound < minCategories) {
        err = t('passwords.validation.min-categories', { minCategories, categoriesFound });
    }
    return err;
};

export const passwordMaxRepeatsValidator = (t, maxRepeats) => (input) => {
    let err = null;
    let currentSequenceChar = null;
    let currentSequenceCounter = 0;
    let repeatsFound = 0;
    [...input].forEach((char) => {
        if (char === currentSequenceChar) {
            currentSequenceCounter++;
        } else {
            if (maxRepeats && currentSequenceCounter > maxRepeats) {
                repeatsFound++;
            }
            currentSequenceChar = char;
            currentSequenceCounter = 1;
        }
    });
    if (maxRepeats && currentSequenceCounter > maxRepeats) {
        repeatsFound++;
    }
    if (repeatsFound) {
        err = t('passwords.validation.max-repeats', { maxRepeats, repeatsFound });
    }
    return err;
};

export const passwordTokenLengthValidator = (t, minTokenLength, userId) => (input) => {
    // token length is checked in LDAP against these attributes: cn, sn, uid, givenname, ou and mail attributes
    let err = null;
    if (input && userId && input.length >= minTokenLength && userId.length >= minTokenLength) {
        for (let i = 0, j = minTokenLength; j <= input.length; i++, j++) {
            const sequence = input.substring(i, j);
            if (userId.indexOf(sequence) !== -1) {
                err = t('passwords.validation.min-token-length', { sequence });
                break;
            }
        }
    }
    return err;
};

export const repeatedPasswordValidator = (t, firstPassword) => (input) => {
    let err = null;
    if (input !== firstPassword) {
        err = t('passwords.validation.no-match');
    }
    return err;
};

export const currentPasswordValidator = (t) => (input) => {
    let err = null;
    if (input === null || input.length === 0) {
        err = t('passwords.validation.empty');
    }
    return err;
};
